import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Smartphone } from 'lucide-react';
import { Link } from 'react-router-dom';

const AppPrivacyPolicy = () => {
  // Check for utm=mobile query parameter
  const urlParams = new URLSearchParams(window.location.search);
  const isMobile = urlParams.get('utm') === 'mobile';
  const privacyContent = `
**Effective Date:** 05/Oct/2025  
**Last Updated:** 13/Jan/2026  

## 1. Introduction & Acceptance

Resiara Software Pvt. Ltd. ("we," "our," or "us") operates the MetroRahi mobile application ("App") and related services. This Privacy Policy explains how we collect, use, disclose, and protect your information when you use MetroRahi.

**By using MetroRahi, you agree to the collection and use of your information in accordance with this Privacy Policy. If you do not agree, please do not use our App.**

MetroRahi is primarily intended for users in India. We comply with applicable Indian laws including:

- Information Technology Act, 2000 & IT Rules, 2011 (SPDI Rules)
- Personal Data Protection Bill (when enacted)
- Any future Indian data protection regulations

For transparency, we also follow global best practices inspired by GDPR (EU) and CCPA (California, USA).

---

## 2. Government Information Disclaimer

MetroRahi is an independent, privately developed application and is not affiliated with, endorsed by, or representing any government entity or metro authority.

The app does not provide official government services and does not claim authorization from any metro organization.

Metro-related information displayed in the app is collected from publicly available official sources such as metro authority websites and is provided for informational purposes only.

For official announcements, fare updates, service changes, or operational notices, users should always refer to the respective metro authority's official website.

To improve accessibility, the app may present information in multiple regional languages. Any translations provided are intended to enhance understanding and do not represent official communication from any metro authority.

Metro-related information in the app may reference publicly available data from official metro authority websites, including but not limited to:

- Delhi Metro Rail Corporation (DMRC): https://www.delhimetrorail.com
- Mumbai Metro Rail Corporation (MMRCL): https://mmrcl.com/en
- Jaipur Metro: https://transport.rajasthan.gov.in/content/transportportal/en/metro.html
- Pune Metro Rail Project: https://www.punemetrorail.org
- Chennai Metro Rail Limited: https://chennaimetrorail.org
- Hyderabad Metro Rail: https://ltmetro.com and https://hmrl.co.in
- Bangalore Metro Rail Corporation Limited (BMRCL): https://english.bmrc.co.in

These links are provided for transparency and reference only.

---

## 3. Information Collected

### 3.1 Personal Information
- Email, Name, Profile picture (when provided via Google/Apple Sign-In)
- User preferences (language, theme, notifications)
- Authentication/session tokens (Firebase/Google/Apple login)

### 3.2 Location Information
- Precise GPS location (to detect nearby metro stations, provide navigation & journey tracking)
- Location history (for route planning and analytics)

### 3.3 Device & Technical Data
- Device ID (App-generated), model, OS version
- App version, IP address, network details
- Crash logs, error reports

### 3.4 Usage Data
- Routes searched, stations selected
- Feature usage statistics
- Interaction with notifications

### 3.5 Communication Data
- Push notification tokens & preferences
- Feedback/support queries

### 3.6 Payment & Subscription Data
- Subscription plan details, purchase history, transaction IDs
- Payment handled only via Apple App Store / Google Play Store
- RevenueCat integration for subscription management and analytics

---

## 4. How We Use Your Information

- **Core Services:** Metro route planning, live tracking, alerts
- **Personalization:** Language, theme, journey suggestions
- **Service Improvement:** Analytics, crash reporting, feature development
- **Communication:** Notifications, support responses, update alerts
- **Legal/Compliance:** Fraud prevention, security checks, regulatory obligations

---

## 5. Data Sharing & Third Parties

We may share limited data with trusted partners:

- **Google** (Ads, Firebase, Sign-In)
- **Apple** (Sign-In, Payments)
- **Mapbox** (Navigation & Maps)
- **RevenueCat** (Subscription management and analytics)
- **Cloud Services** (AWS Cloud for secure storage)

We may also share data if required by law (court orders, govt. requests).

**We do not sell your personal information.**

---

## 6. International Data Protection & Storage

### 6.1 Compliance with International Data Protection Laws

MetroRahi is committed to protecting your personal data in accordance with applicable international data protection regulations, including but not limited to:

- **General Data Protection Regulation (GDPR)** - European Union
- **California Consumer Privacy Act (CCPA)** - California, USA
- **Personal Information Protection and Electronic Documents Act (PIPEDA)** - Canada
- **Australian Privacy Principles (APP)** - Australia
- **Personal Data Protection Act (PDPA)** - Singapore and other jurisdictions
- **Information Technology Act, 2000 & IT Rules, 2011** - India

### 6.2 Data Storage Location

**All personal data collected by MetroRahi is stored securely on Amazon Web Services (AWS) cloud infrastructure located exclusively within the India region (AWS Asia Pacific - Mumbai region).**

This includes:
- User account information (email, name, profile data)
- Location data and journey history
- Device and technical information
- Usage analytics and preferences
- Communication data and notification preferences

**No data is transferred or stored outside the India region without your explicit consent, except as required by law or as necessary for the provision of core services (e.g., third-party authentication services).**

### 6.3 Data Transfer Safeguards

When data must be processed by third-party services (such as Google, Apple, Mapbox, or RevenueCat), we ensure that:
- Data transfers comply with applicable data protection laws
- Third-party services maintain adequate security measures
- Data processing agreements are in place with all service providers
- Your data rights are preserved throughout the processing chain

### 6.4 Your Rights Under International Laws

Depending on your jurisdiction, you may have additional rights including:
- **Right to Access:** Request a copy of your personal data
- **Right to Rectification:** Correct inaccurate or incomplete data
- **Right to Erasure:** Request deletion of your personal data
- **Right to Restrict Processing:** Limit how we use your data
- **Right to Data Portability:** Receive your data in a structured format
- **Right to Object:** Object to certain types of data processing
- **Right to Withdraw Consent:** Withdraw previously given consent

To exercise these rights, please contact us at support.metrorahi@resiara.com with your request and proof of identity.

### 6.5 Legal Basis for Processing (GDPR)

For users in the European Economic Area (EEA), we process your personal data based on:
- **Consent:** When you provide explicit consent for specific processing activities
- **Contract Performance:** To provide the services you have requested
- **Legitimate Interests:** For service improvement, security, and fraud prevention
- **Legal Obligations:** To comply with applicable laws and regulations

---

## 7. Cookies & Tracking

MetroRahi may use:

- **Local Storage** (preferences, cached data)
- **Firebase Analytics** (usage insights)
- **Crashlytics** (bug/error tracking)
- **RevenueCat Analytics** (subscription metrics and user behavior)
- **Google Ads SDK** (ads personalization — can be disabled via device settings)

---

## 8. User Rights

As an Indian user, you have the right to:

- Access, update, or delete your personal data
- Opt-out of marketing/notifications
- Withdraw consent for sensitive data (like location)
- Request deletion of your account and all linked data

### 8.1 Account Deletion

You can delete your account and associated data directly from the app's "Menu(image icon) → My Profile → Delete My Account" option.

---

## 9. Data Security

We use strong safeguards to protect your data:

- **Encryption** (TLS 1.3 in transit, AES-256 at rest)
- **Restricted access** (role-based, MFA for admin)
- **Regular security audits**

In case of a data breach, we will notify users and authorities as per Indian IT Rules.

---

## 10. Data Retention

- **Account Data:** Until user deletes account / inactive for 1 year
- **Location Data:** Retained only for journey purposes (deleted within 72 hours)
- **Analytics:** Retained up to 1 year for service improvement
- **Payment Data:** Stored only by Apple/Google; we never see your card details

---

## 11. Children's Privacy

MetroRahi can be used by users of all ages, including children. However, we recommend parental guidance for users under 13 years of age. Parents should supervise their children's use of the app and review their privacy settings. We do not knowingly collect sensitive personal information from minors without parental consent. Parents can contact us to review, update, or delete their child's data at any time.

---

## 12. Changes to This Policy

We may update this Privacy Policy. Users will be informed via:

- In-app notifications
- App updates or email (if applicable)

---

## 13. Contact Us

**Privacy Queries / Data Deletion Requests**

📧 **Email:** support.metrorahi@resiara.com  
🏢 **Address:** Resiara Software Pvt. Ltd., Jaipur, Rajasthan, India

---

## 14. Platform Compliance & Applicability

### 14.1 iOS and Android Platform Compliance

This Privacy Policy applies to MetroRahi mobile applications available on both:
- **Apple iOS Platform** (distributed through Apple App Store)
- **Google Android Platform** (distributed through Google Play Store)

**By downloading, installing, or using MetroRahi on either iOS or Android platforms, you acknowledge and agree that:**
- This Privacy Policy governs your use of the Service regardless of platform
- All data collection, storage, and processing practices described herein apply uniformly across both platforms
- Platform-specific features (such as Apple Sign-In or Google Sign-In) are subject to the respective platform's privacy policies in addition to this Privacy Policy
- You have read and understood the data practices applicable to your chosen platform

### 14.2 Platform-Specific Data Handling

**iOS Platform:**
- Data collection and processing comply with Apple's App Store Review Guidelines and Privacy Requirements
- Apple Sign-In data is processed in accordance with Apple's Privacy Policy
- Payment and subscription data are handled exclusively by Apple through the App Store

**Android Platform:**
- Data collection and processing comply with Google Play Store policies and requirements
- Google Sign-In data is processed in accordance with Google's Privacy Policy
- Payment and subscription data are handled exclusively by Google through the Play Store

---

## 15. Legal Disclaimers & Limitation of Liability

### 15.1 No Warranty on Data Security

**TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, RESIARA SOFTWARE PVT. LTD. PROVIDES THE SERVICE AND DATA PROCESSING "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.**

We take all reasonable measures to secure your data, but no system is 100% secure. We do not warrant that:
- Your data will be completely secure from unauthorized access
- Data transmission will be error-free or uninterrupted
- Data stored on our systems will never be lost, corrupted, or compromised
- Third-party services will maintain perfect security standards

### 15.2 Limitation of Liability for Data-Related Claims

**TO THE FULLEST EXTENT PERMITTED BY LAW, RESIARA SOFTWARE PVT. LTD., ITS DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY CLAIMS, DAMAGES, LOSSES, OR LEGAL ACTIONS ARISING FROM:**

- **Data Breaches:** Unauthorized access, disclosure, or theft of your personal data, regardless of cause
- **Data Loss:** Accidental deletion, corruption, or loss of your data
- **Third-Party Data Processing:** Any issues arising from data processing by third-party services (Google, Apple, Mapbox, RevenueCat, AWS, etc.)
- **Platform-Specific Issues:** Data-related problems specific to iOS or Android platforms
- **Regulatory Actions:** Any fines, penalties, or legal actions imposed by data protection authorities
- **Compliance Disputes:** Claims related to alleged non-compliance with data protection laws
- **Data Transfer Issues:** Problems arising from cross-border data transfers or third-party data processing
- **System Failures:** Technical failures, cyber-attacks, or force majeure events affecting data security

### 15.3 Maximum Liability Cap

**Our total aggregate liability to you for any and all claims, damages, losses, or legal actions (including data-related claims) arising from or related to this Privacy Policy, your use of MetroRahi, or any data processing activities, shall not exceed the amount you paid to us in the 12 months preceding the claim, or ₹1,000 (Indian Rupees One Thousand), whichever is greater.**

In no event shall we be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses, even if we have been advised of the possibility of such damages.

### 15.4 Third-Party Service Liability

**Use of third-party services (Google, Apple, Mapbox, RevenueCat, AWS, etc.) is subject to their own policies, terms, and liability limitations. Resiara Software Pvt. Ltd. is not responsible for:**
- Any data breaches or security incidents occurring within third-party systems
- Third-party service failures or interruptions
- Legal actions or regulatory penalties imposed on third-party service providers
- Any damages arising from your use of third-party services

### 15.5 Force Majeure and Government Actions

**We are not liable for breaches, data loss, or legal actions resulting from:**
- Cyber-attacks, hacking, or security incidents beyond our reasonable control
- Government orders, regulations, or legal requirements
- Force majeure events (natural disasters, wars, pandemics, etc.)
- Acts of third parties over which we have no control
- Platform-specific restrictions or requirements imposed by Apple or Google

### 15.6 Indemnification

**By using MetroRahi, you agree to indemnify, defend, and hold harmless Resiara Software Pvt. Ltd., its directors, officers, employees, agents, and affiliates from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from:**
- Your violation of this Privacy Policy
- Your misuse of the Service or your data
- Your violation of any applicable data protection laws
- Any claims made by third parties related to your use of the Service

### 15.7 Governing Law and Jurisdiction

**This Privacy Policy is governed by and construed in accordance with the laws of India. Any disputes, claims, or legal actions arising from or related to this Privacy Policy or data processing activities shall be subject to the exclusive jurisdiction of the courts in Jaipur, Rajasthan, India.**

**By using MetroRahi, you irrevocably consent to the exclusive jurisdiction of Indian courts and waive any objection to the venue of any such proceeding in such courts.**

### 15.8 Release of Claims

**To the fullest extent permitted by law, you hereby release and forever discharge Resiara Software Pvt. Ltd. from any and all claims, demands, damages, losses, liabilities, and legal actions (whether known or unknown, suspected or unsuspected) arising from or related to data collection, storage, processing, or security, except as expressly provided in this Privacy Policy.**

---

**By using MetroRahi on iOS or Android platforms, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy, including all limitations of liability and legal protections set forth herein. If you do not agree to these terms, you must immediately stop using the Service and delete the application from your device.**

**Last Updated:** 13/Jan/2026

**Version:** 1.0`;

  return (
    <div className="policy-page app-policy">
      <div className="policy-container">
        <div className="policy-document">
          {/* Header */}
          <motion.div
            className="policy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {!isMobile && (
              <Link to="/" className="back-button">
                <ArrowLeft className="back-icon" />
                <span>Back</span>
              </Link>
            )}
            
            <div className="policy-title-section">
              <h1>Privacy Policy</h1>
              <p>MetroRahi Mobile App Privacy Policy</p>
              <div className="policy-link-section">
                <p className="url-text">https://metrorahi.resiara.com/app/privacy_policy</p>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            className="policy-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="policy-text">
              {privacyContent.split('\n').map((line, index) => {
                if (line.startsWith('# ')) {
                  return <h1 key={index}>{line.substring(2)}</h1>;
                } else if (line.startsWith('## ')) {
                  return <h2 key={index}>{line.substring(3)}</h2>;
                } else if (line.startsWith('### ')) {
                  return <h3 key={index}>{line.substring(4)}</h3>;
                } else if (line.startsWith('**') && line.endsWith('**')) {
                  return <p key={index} className="bold-text">{line.substring(2, line.length - 2)}</p>;
                } else if (line.startsWith('- ')) {
                  return <li key={index}>{line.substring(2)}</li>;
                } else if (line.trim() === '---') {
                  return <hr key={index} className="section-divider" />;
                } else if (line.trim() === '') {
                  return <br key={index} />;
                } else {
                  return <p key={index}>{line}</p>;
                }
              })}
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            className="policy-footer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="company-info">
              <p>Brought to you by <a href="https://resiara.com" target="_blank" rel="noopener noreferrer" className="company-link">Resiara Software Pvt. Ltd.</a></p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AppPrivacyPolicy;
